<template>
  <v-card class="kanban-lane" :class="surfaceClass" variant="flat" border>
    <!-- A cor vive numa barra fina, e não no preenchimento inteiro: é o que deixa o card
         branco ter contraste sem perder a identidade da faixa. A mesma barra marca a janela
         da ficha de um item, e por isso ela é componente. -->
    <ontos-color-accent :color="lane.color"/>

    <!-- O nome e o prazo formam um CONJUNTO, e o "+" fica fora dele.

         É o que decide quem desce quando aperta: o flex quebra no fim da ordem, então, com os
         três no mesmo nível, o que descia era sempre o "+" — a ação principal, que precisa
         estar no mesmo lugar em toda raia. Num conjunto à parte, quem quebra é o prazo, que é
         o item mais largo (ícone, rótulo por extenso e seta) e o menos urgente. A linha de
         fora não quebra nunca: nela ficam o conjunto e o "+", nessa ordem. -->
    <div class="kanban-lane-header d-flex align-center px-3 py-2" :class="titleClass">
      <div class="kanban-lane-heading d-flex align-center flex-wrap">
        <!-- A raia de uma pessoa é a pessoa, como ela aparece em todo o app: a cara e o nome — e não
             só o nome em texto. A contagem continua ao lado. -->
        <div v-if="lane.person" class="kanban-lane-person d-flex align-center mr-auto" :title="countTitle">
          <ontos-person-line :user="lane.person.user" :name="lane.person.name" :photo="lane.person.photo">
            <ontos-count :count="lane.instances.length" :hidden="lane.hiddenCount"/>
          </ontos-person-line>
        </div>
        <h3 v-else class="kanban-lane-title mr-auto" :title="countTitle">{{ lane.name }}
          <ontos-count :count="lane.instances.length" :hidden="lane.hiddenCount"/></h3>
        <finished-period-menu v-if="lane.final" :value="period"
                              @input="$emit('period-change', $event)"/>
      </div>
      <!-- Só o ícone: são um por faixa, e o rótulo por extenso comeria a largura
           que o card precisa. O nome da situação vai no title. -->
      <v-btn v-if="canCreate" class="kanban-lane-create" variant="text" icon size="small"
             :title="createLabel" @click="$emit('create', $event.currentTarget)">
        <v-icon size="small">mdi-plus</v-icon>
      </v-btn>
    </div>

    <!-- O vuedraggable 4 desenha um item por vez, pelo slot #item, e arrasta o ELEMENTO
         que esse slot produz. Por isso o div: repassado direto, o slot de quem usa a
         faixa chegaria como fragmento, cujo elemento é um nó de texto vazio. Quem usa
         diz só como desenhar um card, pelo slot com escopo; a chave vem do item-key. -->
    <!-- `fallback-on-body`: no toque o Sortable não usa o arrasto nativo do navegador — desenha
         um CLONE do card e o posiciona com `position: fixed`, em coordenadas de janela. Só que
         o `.v-slide-group__container` do modo deslizante tem `contain: content`, e contenção de
         layout torna o elemento um bloco de contenção para descendentes fixos: as coordenadas
         passavam a valer a partir do container, e o clone saía deslocado do dedo (e recortado
         ao passar da faixa, pela mesma contenção). Pendurado no `body`, ele volta a ser medido
         contra a janela. No mouse nada muda: ali quem desenha o arrasto é o navegador. -->
    <!-- `force-fallback`: um motor de arrasto só, em toda entrada. Sem isto o Sortable marca o
         card como `draggable` para usar o arrasto NATIVO do navegador — e no toque o Chromium
         dispara esse arrasto nativo sozinho, com meio segundo de dedo parado. Passavam a
         existir dois arrastos ao mesmo tempo: o do navegador, que desenha a própria imagem
         (maior que o card, em escala) e não solta onde o Sortable espera. É o que fazia o
         gesto depender de QUANTO tempo se segurava antes de mover. -->
    <!-- O menu de contexto morre no toque, e só nele. O card é um `<a>`, e o toque longo num
         link abre o menu do navegador — que no Android é o que aparece justamente quando se
         segura o dedo para arrastar. No mouse ele fica: o botão direito de um link serve para
         abrir a ficha em outra aba, e tirar isso seria perder um gesto por causa de outro. -->
    <!-- `disabled` é o único jeito de tirar o ARRASTO: ele é alteração sem botão, e não há
         o que deixar de desenhar. Sem permissão de editar — ambiente encerrado, ou quem só
         visualiza — o card continua clicável e abre a ficha; o que some é poder movê-lo. -->
    <!-- `settlingDrag` e `settleDrop`: o card assenta ao ser solto, deslizando de onde o clone do
         arrasto estava até o lugar em que caiu — o mesmo das propriedades da coleção. -->
    <draggable v-model="lane.instances" item-key="_instance" class="kanban-lane-list px-1 px-sm-2"
               v-bind="settlingDrag"
               :disabled="!canWrite"
               :group="group" :filter="'.no-drag'" ghost-class="ghost"
               :delay="150" :delay-on-touch-only="true"
               @pointerdown="pointerType = $event.pointerType"
               @contextmenu="onContextMenu($event)"
               @start="$emit('drag-start')"
               @end="settleDrop($event)"
               @change="$emit('change', $event)">
      <!-- O card que a fórmula de acesso não deixa editar não arrasta (o `no-drag` do `filter`):
           mover é editar, e o servidor recusaria — o do item, ou o de um campo que o arrasto muda -->
      <!-- `data-card`: é por ele que o quadro reconhece o card depois de reagrupar, para levá-lo
           do lugar antigo ao novo (ver o `animateFrom`). -->
      <template #item="{ element }">
        <div :class="{'no-drag': !movable(element)}" :data-card="element._instance">
          <slot :instance="element"/>
        </div>
      </template>
    </draggable>
  </v-card>
</template>

<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import draggable from "vuedraggable";
import Count from "@/components/shared/Count.vue";
import FinishedPeriodMenu from "@/components/live/instance/FinishedPeriodMenu.vue";
import {FinishedPeriod} from "@/utils/finishedPeriod";
import {LaneModel} from "@/utils/kanbanGrouping";
import {surfaceClass, titleClass} from "@/utils/colors";
import ColorAccent from "@/components/shared/ColorAccent.vue";
import PersonLine from "@/components/shared/PersonLine.vue";
import {fieldEditable, itemEditable} from "@/types/Idea";
import {settleDrop, settlingDrag} from "@/utils/motion";

/**
 * Uma faixa do tabuleiro: cabeçalho com o contador e as ações, e a lista que recebe
 * o arrasto.
 *
 * Vive fora do KanbanBoard porque o sandbox de desenvolvimento desenha o mesmo
 * tabuleiro com dados fabricados — antes eram duas cópias da marcação, e toda
 * mudança de aparência precisava ser feita nas duas. O que varia entre os dois é só
 * o conteúdo de cada item, que entra pelo slot.
 *
 * Não decide nada: quem agrupa, persiste e recarrega é o hospedeiro. Daqui sobem
 * apenas os três gestos — criar, mudar o prazo e arrastar.
 */
@Component({
  //drag-start: o quadro precisa saber de ONDE o card saiu. O `change` só conta o destino —
  //o `removed` dispara aqui, mas não sabe para onde o card foi (ver o onCardDrop do quadro).
  emits: ['period-change', 'create', 'change', 'drag-start'],
  name: 'ontos-kanban-lane',
  components: {
    draggable,
    'finished-period-menu': FinishedPeriodMenu,
    'ontos-count': Count,
    'ontos-color-accent': ColorAccent,
    'ontos-person-line': PersonLine
  }
})
class KanbanLane extends Vue {
  //expostas ao template: função de módulo não é enxergada de lá
  settlingDrag = settlingDrag
  settleDrop = settleDrop

  /** Os campos que arrastar um card muda — as dimensões do quadro. */
  @Prop({type: Array, default: () => []}) readonly movedFields!: string[]

  /** O card se move se quem olha edita o item e cada campo que o arrasto muda (`fieldEditable`). */
  movable(item: any): boolean {
    return itemEditable(item) && this.movedFields.every(alias => fieldEditable(item, {alias}))
  }

  /**
   * A raia já montada pelo agrupamento.
   *
   * Chamava-se `state` de quando o Kanban só agrupava por situação. Hoje a raia pode ser uma
   * opção de seleção ou um item apontado por um relacionamento, e com `_state`, `State` e uma
   * raia chamada "Prioridade Alta" convivendo, o nome antigo era convite a erro. O conteúdo é
   * o mesmo de sempre, e a faixa continua lendo só cinco campos dele.
   */
  @Prop({type: Object as () => LaneModel, required: true}) readonly lane!: LaneModel
  /** Prazo dos terminados. Só as faixas finais usam. */
  @Prop({type: Object, default: null}) readonly period!: FinishedPeriod | null
  /** Grupo de arrasto, para dois tabuleiros abertos não trocarem cards. */
  @Prop({type: String, required: true}) readonly group!: string
  @Prop({type: Boolean, default: true}) readonly canCreate!: boolean
  /** Os cards podem ser movidos? Sem isto o arrasto não pega. Quem responde é o tabuleiro. */
  @Prop({type: Boolean, default: true}) readonly canWrite!: boolean
  @Prop({type: String, default: ''}) readonly createLabel!: string
  /** Quantos itens o prazo escondeu, escrito por extenso. */
  @Prop({type: String, default: ''}) readonly countTitle!: string

  /** Como o último gesto começou. É o que separa o toque longo do botão direito. */
  pointerType = 'mouse'

  /**
   * Toque longo não abre menu de contexto.
   *
   * No Android é ele que aparece quando se segura o dedo para arrastar — o card é um `<a>`, e o
   * navegador oferece o menu do link. No mouse o menu fica: ali o botão direito é um gesto
   * próprio, que abre a ficha em outra aba, e não disputa com o arrasto.
   *
   * O tipo vem do `pointerdown` anterior, e não do próprio evento: nem todo navegador traz
   * `pointerType` no `contextmenu`, e o que se quer saber é como o gesto COMEÇOU.
   */
  onContextMenu(event: Event) {
    if (this.pointerType !== 'mouse') event.preventDefault()
  }


  /**
   * Os tons da cor da raia, pelo mesmo cálculo que o menu de raias usa na miniatura de cada
   * uma — eram duas cópias, e agora é uma função só, no kanbanLayout.
   *
   * Valor sem cor devolve vazio e a raia fica neutra: é o caso de uma dimensão de
   * relacionamento, onde o valor não tem cor nenhuma.
   */
  get surfaceClass(): string {
    return surfaceClass(this.lane.color)
  }

  get titleClass(): string {
    return titleClass(this.lane.color)
  }
}

export default toNative(KanbanLane)
</script>

<!-- NÃO scoped, e de propósito: a regra vale para o `v-col` que HOSPEDA a faixa, que é
     elemento do pai e por isso fora do alcance de um estilo scoped daqui. Mora neste
     arquivo mesmo assim porque é a faixa que impõe a exigência — quem a usa dentro de
     uma grade precisa dela —, e assim os dois hospedeiros (o tabuleiro e o sandbox) a
     recebem sem uma segunda cópia para sair de sincronia. -->
<style>
/* As faixas ficam sempre lado a lado, numa fileira só — sem espaço para todas, é o conjunto
   que passa a deslizar, e não a fileira que quebra.

   O `.v-col` não declara min-width para a coluna que cresce, então vale o `auto` do flex: ela
   se recusa a encolher abaixo do conteúdo, e assim que a fatia de cada uma fica menor que esse
   mínimo a última pula para uma segunda fileira. É o que acontecia em resolução mediana, com o
   número de faixas dividindo mal a largura.

   Com min-width: 0 a base volta a ser zero de verdade e nenhuma quantidade de faixas provoca
   quebra. Deslizando não há o que quebrar — lá a coluna tem a largura mínima e a fileira
   transborda de propósito, o que o min-width não atrapalha porque a base vem do flex-basis. */
.kanban-column {
  min-width: 0;
}

/* O azul do destaque do Kanban, num lugar só. É a cor que o lugar do card ganha
   enquanto ele é arrastado, e a mesma que a linha do menu de raias usa no hover — os
   dois dizem "é aqui", e repetir o hexadecimal nos dois arquivos seria a receita para
   eles se separarem na primeira vez que alguém quisesse ajustar o tom. */
:root {
  --ontos-kanban-highlight: #cce0ff;
}
</style>

<style scoped>
/* Altura igual entre as faixas: o v-col já estica (align-items: stretch é o padrão
   do v-row), mas o card parava no tamanho do conteúdo e a faixa com dois itens
   ficava mais baixa que a vizinha com oito. Com height: 100% e coluna flex, a lista
   ocupa o que sobra e todas acompanham a mais alta — inclusive ao arrastar um card
   de uma para outra. Vale igual no grupo deslizante: o conteúdo dele também é um flex com
   align-items: stretch, e as faixas continuam acompanhando a mais alta. */
.kanban-lane {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.kanban-lane-header {
  flex: 0 0 auto;
}

/* O conjunto fica com toda a largura que o "+" não usa, e o min-width: 0 é o que o deixa
   encolher: sem ele o mínimo automático de um item flex é o min-content, e o conjunto se
   recusaria a ficar menor que o nome inteiro mais o prazo — empurrando o "+" para fora. */
.kanban-lane-heading {
  flex: 1 1 auto;
  min-width: 0;
}

/* O "+" não encolhe — é alvo de clique, e espremê-lo é pior do que cortar um texto que continua
   inteiro no title.

   E acompanha a linha do NOME, não o centro do conjunto: com o prazo embaixo, centralizado ele
   ficaria no meio das duas linhas. Numa linha só nada muda, porque ele é o item mais alto dela. */
.kanban-lane-create {
  flex: 0 0 auto;
  align-self: flex-start;
}

/* Uma linha só: o contador é o que não pode quebrar para longe do nome.

   O min-width: 0 é o que permite as reticências: sem ele o mínimo automático de um item flex é
   o min-content, e o nome se recusaria a ficar menor que a palavra mais longa dele. */
.kanban-lane-title {
  min-width: 0;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Encolhe como o título: o nome e o e-mail cortam com reticências, e não empurram o "+" */
.kanban-lane-person {
  min-width: 0;
}


/* pb: 0 porque o espaço do fim vem da margem do último card; repetir aqui daria
   duas vezes o respiro. O min-height é o que deixa a faixa vazia aceitar um card. */
/* A outra metade do mesmo problema: com o dedo parado sobre o card, o navegador começa a
   SELEÇÃO de texto e oferece o menu de contexto — e é a seleção que ele passa a arrastar,
   com uma imagem que não é a do card. Herdado, isto vale para o card inteiro sem `:deep()`.

   O preço é não dar para selecionar o texto de um card com o dedo. É o que todo quadro faz,
   pelo mesmo motivo, e o texto continua selecionável na ficha do item. */
.kanban-lane-list {
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  flex: 1 1 auto;
  min-height: 56px;
  padding-bottom: 0;
}

/* :deep() porque o card vem pelo slot e carrega o escopo de QUEM preenche o slot,
   não o desta folha de estilo. Sem isso o arrasto voltaria a não dar retorno
   visual nenhum — e a regra teria de ser copiada em cada hospedeiro. */
.kanban-lane-list :deep(.ghost) {
  background-color: var(--ontos-kanban-highlight) !important;
}

.kanban-lane-list :deep(.ghost *) {
  visibility: hidden;
}
</style>
